import React, { useEffect, useMemo, useState } from 'react'
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'

import {
  ArrowClockwise,
  CaretDown,
  CaretUp,
  PencilFill,
  PlusCircleFill,
  ShareFill,
  TrashFill,
} from 'react-bootstrap-icons'

import Navbar from '../../../components/navbar/navbar'
import { CustomButton } from '../../../components/CustomButton'
import { CustomSpinner } from '../../../components/CustomSpinner'
import { useOrderTable } from '../../../hooks/useOrderTable'
import PessoasService from '../../../services/database/PessoasService'
import { isLojaBelaHome } from '../../../config/globalVariables'
import removeNonCharacters from '../../../utils/removeNonCharacters'

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100]

export function Clientes() {
  const [customers, setCustomers] = useState([])
  const [termSearch, setTermSearch] = useState('pes_razao')
  const [search, setSearch] = useState('')
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [currentPage, setCurrentPage] = useState(0)
  const [isCustomersLoading, setIsCustomersLoading] = useState(true)

  const navigate = useNavigate()

  const {
    sortedArray: sortedCustomers,
    check,
    handleOrganize,
    orderType,
  } = useOrderTable(customers, 'cod_pessoa')

  const filteredCustomers = useMemo(() => {
    if (!search.trim()) return sortedCustomers

    return sortedCustomers.filter((c) => {
      if (termSearch === 'pes_cnpj_cpf') {
        return removeNonCharacters(c.pes_cnpj_cpf || '').includes(
          removeNonCharacters(search),
        )
      }
      return String(c[termSearch] || '')
        .toLowerCase()
        .includes(search.toLowerCase())
    })
  }, [search, termSearch, sortedCustomers, orderType.field, orderType.type])

  // Reset página ao filtrar
  useEffect(() => { setCurrentPage(0) }, [search, termSearch])

  const pageCount = Math.ceil(filteredCustomers.length / itemsPerPage)

  const currentItems = useMemo(() => {
    const start = currentPage * itemsPerPage
    return filteredCustomers.slice(start, start + itemsPerPage)
  }, [filteredCustomers, currentPage, itemsPerPage])

  function getPaginationItems() {
    if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i)
    const items = []
    const delta = 2
    for (let i = 0; i < pageCount; i++) {
      if (i === 0 || i === pageCount - 1 || Math.abs(i - currentPage) <= delta) {
        items.push(i)
      } else if (Math.abs(i - currentPage) === delta + 1) {
        items.push('...')
      }
    }
    return items
  }

  async function loadCustomers() {
    setIsCustomersLoading(true)
    try {
      const data = await PessoasService.getAll()
      setCustomers(data)
    } catch (error) {
      console.error(error)
      toast.error('Houve um erro ao listar os clientes!')
    } finally {
      setIsCustomersLoading(false)
    }
  }

  useEffect(() => { loadCustomers() }, [])

  const COLS = [
    { field: 'cod_pessoa',     label: 'Código',        width: 80 },
    { field: 'pes_razao',      label: 'Razão Social' },
    { field: 'pes_fantasia',   label: 'Nome Fantasia' },
    { field: null,             label: 'CPF / CNPJ',    width: 140 },
    { field: null,             label: 'Telefone',      width: 130 },
    { field: 'pes_logradouro', label: 'Logradouro' },
    { field: 'pes_cidade',     label: 'Cidade',        width: 130 },
    { field: null,             label: 'UF',            width: 55,  align: 'center' },
    { field: null,             label: 'Ações',         width: isLojaBelaHome ? 126 : 96, align: 'center' },
  ]

  return (
    <>
      <Navbar />
      <div className="mt-page p-4" style={{ background: '#fff' }}>

        {/* Cabeçalho */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: '1.25rem' }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 2 }}>Clientes</h1>
            <p style={{ fontSize: 13, color: '#888', margin: 0 }}>
              {customers.length} cliente{customers.length !== 1 ? 's' : ''} cadastrado{customers.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <CustomButton color="#088c04" onClick={() => navigate('/clientes/novo')}>
              <PlusCircleFill size={14} /> Novo cliente
            </CustomButton>
            <CustomButton color="#007FFF" onClick={loadCustomers}>
              <ArrowClockwise size={14} /> Recarregar
            </CustomButton>
          </div>
        </div>

        {/* Barra de filtros */}
        <div style={{ display: 'flex', gap: 8, marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={labelStyle}>Por página</div>
            <Form.Select
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(0) }}
              style={{ width: 90, fontSize: 13 }}
            >
              {ITEMS_PER_PAGE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </Form.Select>
          </div>

          <div>
            <div style={labelStyle}>Pesquisar por</div>
            <Form.Select
              value={termSearch}
              onChange={(e) => setTermSearch(e.target.value)}
              style={{ width: 170, fontSize: 13 }}
            >
              <option value="pes_razao">Razão Social</option>
              <option value="pes_cnpj_cpf">CPF / CNPJ</option>
              <option value="pes_fantasia">Nome Fantasia</option>
              <option value="pes_logradouro">Logradouro</option>
            </Form.Select>
          </div>

          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={labelStyle}>Pesquisa</div>
            <Form.Control
              value={search}
              placeholder="Pesquisar..."
              onChange={(e) => setSearch(e.target.value)}
              style={{ fontSize: 13 }}
            />
          </div>

          {search && (
            <button
              onClick={() => setSearch('')}
              style={{ alignSelf: 'flex-end', padding: '7px 12px', fontSize: 12, border: '1px solid #dee2e6', borderRadius: 6, background: '#fff', color: '#888', cursor: 'pointer', marginBottom: 1 }}
            >
              ✕ Limpar
            </button>
          )}

          <span style={{ alignSelf: 'flex-end', fontSize: 12, color: '#888', paddingBottom: 6, marginLeft: 'auto' }}>
            {filteredCustomers.length} resultado{filteredCustomers.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Tabela */}
        {isCustomersLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem 0' }}>
            <CustomSpinner size={60} />
          </div>
        ) : (
          <>
            <div style={{ border: '1px solid #e8e8e8', borderRadius: 8, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed', minWidth: 860 }}>
                <colgroup>
                  {COLS.map((col, i) => (
                    <col key={i} style={{ width: col.width ?? undefined }} />
                  ))}
                </colgroup>
                <thead>
                  <tr style={{ background: '#f7f7f8', borderBottom: '2px solid #e8e8e8' }}>
                    {COLS.map(({ field, label, align = 'left' }) => (
                      <th
                        key={label}
                        onClick={field ? () => handleOrganize(field) : undefined}
                        style={{
                          padding: '10px 12px',
                          textAlign: align,
                          fontSize: 11.5,
                          fontWeight: 600,
                          color: '#555',
                          whiteSpace: 'nowrap',
                          cursor: field ? 'pointer' : 'default',
                          userSelect: 'none',
                          letterSpacing: '0.02em',
                        }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                          {label}
                          {field && (
                            <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1 }}>
                              <CaretUp size={9} color={check(field, 'asc')} />
                              <CaretDown size={9} color={check(field, 'desc')} />
                            </span>
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length === 0 ? (
                    <tr>
                      <td colSpan={COLS.length} style={{ textAlign: 'center', padding: '2.5rem', color: '#aaa', fontSize: 13 }}>
                        Nenhum cliente encontrado para "{search}"
                      </td>
                    </tr>
                  ) : (
                    currentItems.map((customer, idx) => (
                      <CustomerRow
                        key={customer.cod_pessoa}
                        customer={customer}
                        idx={idx}
                        loadCustomers={loadCustomers}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {pageCount > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '1rem', flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontSize: 12, color: '#888' }}>
                  Mostrando {currentPage * itemsPerPage + 1}–{Math.min((currentPage + 1) * itemsPerPage, filteredCustomers.length)} de {filteredCustomers.length}
                </span>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <PagBtn disabled={currentPage === 0} onClick={() => setCurrentPage((p) => p - 1)}>‹ Anterior</PagBtn>
                  {getPaginationItems().map((item, idx) =>
                    item === '...'
                      ? <span key={`e${idx}`} style={{ padding: '0 6px', color: '#aaa', fontSize: 13 }}>…</span>
                      : <PagBtn key={item} active={item === currentPage} onClick={() => setCurrentPage(item)}>{item + 1}</PagBtn>
                  )}
                  <PagBtn disabled={currentPage >= pageCount - 1} onClick={() => setCurrentPage((p) => p + 1)}>Próxima ›</PagBtn>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}

// ---- Linha da tabela ----

function CustomerRow({ customer, idx, loadCustomers }) {
  const [isDeleteLoading, setIsDeleteLoading] = useState(false)
  const [isExportLoading, setIsExportLoading] = useState(false)
  const navigate = useNavigate()

  async function handleExport() {
    setIsExportLoading(true)
    try {
      await PessoasService.exportToMatriz(customer.cod_pessoa)
      toast.success('Cliente exportado com sucesso!')
    } catch (error) {
      console.error(error)
      toast.error('Erro ao exportar o cliente!')
    } finally {
      setIsExportLoading(false)
    }
  }

  async function handleDelete() {
    const { isConfirmed } = await Swal.fire({
      titleText: `Excluir ${customer.pes_razao}?`,
      icon: 'warning',
      text: 'Essa ação é irreversível!',
      showCancelButton: true,
      confirmButtonText: 'Excluir',
      confirmButtonColor: '#e70f0f',
      cancelButtonText: 'Cancelar',
    })

    if (!isConfirmed) return

    setIsDeleteLoading(true)
    try {
      await PessoasService.delete(customer.cod_pessoa)
      toast.success('Cliente excluído com sucesso!')
      loadCustomers()
    } catch (error) {
      console.error(error)
      toast.error('Erro ao excluir cliente!')
    } finally {
      setIsDeleteLoading(false)
    }
  }

  return (
    <tr
      style={{
        background: idx % 2 === 0 ? '#fff' : '#fafafa',
        borderBottom: '1px solid #f0f0f0',
        transition: 'background 0.1s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f4ff' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#fafafa' }}
    >
      <td style={td()}>
        <span style={{ color: '#aaa', fontSize: 12 }}>#{customer.cod_pessoa}</span>
      </td>
      <td style={td()}>
        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={customer.pes_razao}>
          {customer.pes_razao}
        </span>
      </td>
      <td style={td()}>
        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={customer.pes_fantasia}>
          {customer.pes_fantasia || <span style={{ color: '#ccc' }}>—</span>}
        </span>
      </td>
      <td style={td()}>
        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{customer.pes_cnpj_cpf || '—'}</span>
      </td>
      <td style={td()}>{customer.pes_telefone || <span style={{ color: '#ccc' }}>—</span>}</td>
      <td style={td()}>
        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={customer.pes_logradouro}>
          {customer.pes_logradouro || <span style={{ color: '#ccc' }}>—</span>}
        </span>
      </td>
      <td style={td()}>
        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {customer.pes_cidade || <span style={{ color: '#ccc' }}>—</span>}
        </span>
      </td>
      <td style={td('center')}>
        {customer.pes_uf
          ? <span style={{ fontSize: 11, fontWeight: 700, color: '#555', background: '#f1f1f1', padding: '2px 6px', borderRadius: 4 }}>{customer.pes_uf}</span>
          : <span style={{ color: '#ccc' }}>—</span>
        }
      </td>
      <td style={td('center')}>
        <div style={{ display: 'inline-flex', gap: 5 }}>
          {isLojaBelaHome && (
            <OverlayTrigger overlay={<Tooltip>Exportar para Matriz</Tooltip>}>
              <ActionBtn color="#0641d4" onClick={handleExport} isLoading={isExportLoading}>
                <ShareFill size={13} color="#0641d4" />
              </ActionBtn>
            </OverlayTrigger>
          )}

          <OverlayTrigger overlay={<Tooltip>Editar</Tooltip>}>
            <ActionBtn color="#ec630a" onClick={() => navigate(`/clientes/editar/${customer.cod_pessoa}`)}>
              <PencilFill size={13} color="#ec630a" />
            </ActionBtn>
          </OverlayTrigger>

          <OverlayTrigger overlay={<Tooltip>Excluir</Tooltip>}>
            <ActionBtn color="#e70f0f" onClick={handleDelete} isLoading={isDeleteLoading}>
              <TrashFill size={13} color="#e70f0f" />
            </ActionBtn>
          </OverlayTrigger>
        </div>
      </td>
    </tr>
  )
}

// ---- Helpers ----

const labelStyle = {
  fontSize: 11,
  color: '#888',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 4,
  fontWeight: 600,
}

function td(align = 'left') {
  return {
    padding: '9px 12px',
    textAlign: align,
    fontSize: 13,
    color: '#222',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  }
}

function ActionBtn({ children, color, onClick, isLoading = false }) {
  const [hovered, setHovered] = useState(false)
  return (
    <button
      onClick={onClick}
      disabled={isLoading}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: 28,
        height: 28,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 6,
        border: `1px solid ${color}44`,
        background: hovered ? color : `${color}18`,
        color: hovered ? '#fff' : color,
        cursor: isLoading ? 'wait' : 'pointer',
        transition: 'all 0.15s',
        flexShrink: 0,
        opacity: isLoading ? 0.6 : 1,
      }}
    >
      {React.cloneElement(children, { color: hovered ? '#fff' : color })}
    </button>
  )
}

function PagBtn({ children, active, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '5px 10px',
        fontSize: 12,
        borderRadius: 6,
        border: '1px solid',
        borderColor: active ? '#0d6efd' : '#dee2e6',
        background: active ? '#0d6efd' : '#fff',
        color: active ? '#fff' : disabled ? '#aaa' : '#333',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: active ? 600 : 400,
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.15s',
      }}
    >
      {children}
    </button>
  )
}